import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO"; // Import the SEO component
import HelpButton from "../components/HelpButton";
import { allTerms } from "../data/terms";

// Define the alphabet array for the index
const alphabet = [
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "I",
  "J",
  "K",
  "L",
  "M",
  "N",
  "O",
  "P",
  "Q",
  "R",
  "S",
  "T",
  "U",
  "V",
  "W",
  "X",
  "Y",
  "Z",
  "#",
];

const terms = {
  A: [
    {
      term: "Aarding",
      definition: "Een verbinding met de aarde voor elektrische veiligheid.",
    },
    {
      term: "Aardpen",
      definition:
        "Een metalen staaf die in de grond wordt geslagen voor het maken van een aardverbinding.",
    },
    {
      term: "Aardedraad",
      definition: "De draad die zorgt voor de verbinding met de aarde.",
    },
    {
      term: "Aarderail",
      definition:
        "Een koperen rail in de meterkast waar alle aarddraden samenkomen.",
    },
    {
      term: "Aardlekautomaat",
      definition:
        "Een combinatie van een aardlekschakelaar en een installatieautomaat.",
    },
    {
      term: "Aardlekschakelaar",
      definition:
        "Een veiligheidsschakelaar die de stroom uitschakelt bij een lekstroom naar de aarde.",
    },
    {
      term: "ABB",
      definition:
        "Een belangrijke fabrikant van elektrische componenten en installatiemateriaal.",
    },
    {
      term: "AC (wisselstroom)",
      definition:
        "Elektrische stroom waarvan de stroomrichting periodiek wisselt.",
    },
    {
      term: "Adereindhulzen",
      definition:
        "Hulzen die worden gebruikt om het uiteinde van een draad te beschermen en te verstevigen.",
    },
    {
      term: "Afdekplaat",
      definition: "Een plaat die elektrische componenten afschermt.",
    },
    {
      term: "Afdekraam",
      definition:
        "Een frame dat wordt gebruikt om schakelmateriaal netjes af te werken.",
    },
    {
      term: "Afmonteren",
      definition: "Het aansluiten en afwerken van elektrische installaties.",
    },
    { term: "Ampère", definition: "De eenheid van elektrische stroomsterkte." },
    { term: "Armaturen", definition: "Behuizingen voor verlichting." },
    {
      term: "Attema",
      definition: "Een fabrikant van elektrotechnische installatieproducten.",
    },
    {
      term: "Automaat",
      definition:
        "Een zekering die automatisch uitschakelt bij overbelasting of kortsluiting.",
    },
    {
      term: "Afzekeren",
      definition:
        "Het beschermen van een elektrische installatie met een zekering of installatieautomaat.",
    },
  ],
  B: [
    {
      term: "B16",
      definition: "Een installatieautomaat van 16 ampère met B-karakteristiek.",
    },
    { term: "Beltrafo", definition: "Een transformator voor deurbellen." },
    {
      term: "Beugels",
      definition: "Bevestigingsmaterialen voor elektrische installaties.",
    },
    {
      term: "Bewegingssensor",
      definition:
        "Een sensor die beweging detecteert en verlichting kan activeren.",
    },
    {
      term: "Bliksemafleider",
      definition: "Een installatie die beschermt tegen blikseminslag.",
    },
    {
      term: "Bouwtekening",
      definition: "Een technische tekening van een elektrische installatie.",
    },
    {
      term: "Buitenverlichting",
      definition: "Verlichting voor gebruik buitenshuis.",
    },
    {
      term: "Boorgatdoos",
      definition: "Een inbouwdoos die in een geboord gat wordt geplaatst.",
    },
    {
      term: "Busbar",
      definition:
        "Een metalen geleider voor het verdelen van elektrische energie binnen een installatie.",
    },
  ],
  C: [
    {
      term: "CAI",
      definition: "Centrale Antenne Inrichting voor televisie en radio.",
    },
    {
      term: "CAT6",
      definition: "Een categorie netwerkkabel voor dataverkeer.",
    },
    {
      term: "CAT7",
      definition: "Een verbeterde categorie netwerkkabel voor dataverkeer.",
    },
    {
      term: "CEE",
      definition: "Een standaard voor industriële stekkers en contactdozen.",
    },
    {
      term: "Centraaldoos",
      definition: "Een aansluitdoos voor plafondverlichting.",
    },
    {
      term: "Circuitonderbreker",
      definition: "Een apparaat dat een elektrisch circuit kan onderbreken.",
    },
    {
      term: "Coax",
      definition:
        "Een type kabel voor het transport van hoogfrequente signalen.",
    },
    {
      term: "Combinatieschakeling",
      definition: "Een schakeling met meerdere schakelaars voor één lichtpunt.",
    },
    {
      term: "Connector",
      definition:
        "Een verbindingsstuk voor elektrische of elektronische componenten.",
    },
    { term: "Coulomb", definition: "De eenheid van elektrische lading." },
    {
      term: "Contactdoos",
      definition:
        "Een wandcontactdoos waarin een stekker kan worden geplaatst.",
    },
    {
      term: "Contactor",
      definition:
        "Een elektrisch relais dat gebruikt wordt om grote vermogens te schakelen.",
    },
  ],
};

function Begrippen() {
  const [searchTerm, setSearchTerm] = useState("");

  // Group terms by first letter
  const groupedTerms = allTerms.reduce((acc, term) => {
    const firstLetter = term.term[0].toUpperCase();
    if (!acc[firstLetter]) {
      acc[firstLetter] = [];
    }
    acc[firstLetter].push(term);
    return acc;
  }, {});

  // Filter terms based on search
  const filteredTerms = searchTerm
    ? allTerms.filter(
        (term) =>
          term.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
          term.definition.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  return (
    <div className="pt-[160px] min-h-screen bg-white page-transition  md:pt-[133px]">
      <SEO
        title="Begrippenlijst elektrotechniek - elektricieninarnhem026.nl"
        description="Vind snel de betekenis van elektrotechnische termen en begrippen. Begrippenlijst elektrotechniek - elektricieninarnhem026.nl"
        image="/images/banner.png"
      />
      <Header />
      <section className="mx-auto w-[calc(100%-2.5rem)] max-w-[1088px] mb-12 md:mb-20 pt-16 md:pt-24">
        <h1 className="font-bold text-black leading-[1.25] text-[28px] lg:text-[36px] text-[#17354A] block mb-4 leading-[35px] text-[2rem] md:inline-block md:mb-5 md:text-[44px] md:leading-[42px] lg:block">
          Alle begrippen
        </h1>
        <div className="text-black leading-[1.56] text-[16px] md:text-[18px] font-normal text-[#17354A] mb-8">
          Vind snel de betekenis van elektrotechnische termen en begrippen.
        </div>

        {/* Search Bar */}
        <div className="mb-8">
          <div className="relative">
            <input
              type="text"
              placeholder="Zoek een begrip..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full bg-white text-black appearance-none text-base outline-none border-solid rounded-xl border border-[#E5E7EB] transition-all shadow-none duration-150 hover:border-[#17354A]/50 focus:border-[#17354A] box-border h-14 px-4"
            />
          </div>
        </div>

        {/* Search Results */}
        {searchTerm && (
          <div className="mb-12">
            <h2 className="font-bold text-[24px] lg:text-[36px] text-[#17354A] mb-6">
              Zoekresultaten
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {filteredTerms.map((term) => (
                <Link
                  key={term.slug}
                  to={`/begrippen/${term.slug}`}
                  className="block group p-6 bg-white rounded-2xl border border-[#E5E7EB] hover:border-gray-300 transition-colors duration-150"
                >
                  <h3 className="text-[#F47B20] font-medium text-lg mb-2 group-hover:underline">
                    {term.term}
                  </h3>
                  <p className="text-[#17354A] text-base leading-relaxed mb-4">
                    {term.definition}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Alphabet Index */}
        <div className="flex flex-wrap gap-2 mb-12 md:mb-16">
          {alphabet.map((letter) => (
            <a
              key={letter}
              href={`#${letter}`}
              className={`w-8 h-8 flex items-center justify-center rounded-lg text-base font-medium transition-colors ${
                groupedTerms[letter]
                  ? "text-[#FFD100] hover:bg-[#FFD100] hover:text-white"
                  : "text-gray-300 cursor-not-allowed"
              }`}
            >
              {letter}
            </a>
          ))}
        </div>

        {/* Terms List */}
        <div className="space-y-16">
          {Object.entries(groupedTerms).map(([letter, terms]) => (
            <div key={letter} id={letter}>
              <h2 className="font-bold text-[24px] lg:text-[36px] text-[#17354A] mb-6">
                {letter}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {terms.map((item, index) => (
                  <Link
                    key={index}
                    to={`/begrippen/${item.slug}`}
                    className="flex items-center p-6 bg-white rounded-2xl border border-[#E5E7EB] hover:border-[#17354A]/50 transition-colors duration-150"
                  >
                    <span className="text-[#17354A] text-lg font-medium">
                      {item.term}
                    </span>
                    <ChevronRight className="w-5 h-5 text-[#17354A] ml-auto" />
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
      <HelpButton />
      <Footer />
    </div>
  );
}

export default Begrippen;
